import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { API_URL } from '../config/api';
import { useTranslation } from '../hooks/useTranslation';
import FooterNote from './FooterNote';
import './css/contactpage.css';

const ContactPage = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings/contact`);
        setContact(res.data?.contact || {});
      } catch (e) {
        setContact({});
      } finally {
        setLoading(false);
      }
    };
    fetchContact();
  }, []);

  const items = useMemo(() => {
    if (!contact) return [];
    const address = language === 'ar' ? contact.address_ar : contact.address_en;
    const rows = [
      { key: 'phone', label: t('contact.phone'), value: contact.phone, href: contact.phone ? `tel:${contact.phone}` : '' , icon: PhoneIcon },
      { key: 'whatsapp', label: t('contact.whatsapp'), value: contact.whatsapp, href: contact.whatsapp ? `https://wa.me/${String(contact.whatsapp).replace(/[^\d]/g, '')}` : '', icon: ChatBubbleLeftRightIcon },
      { key: 'email', label: t('contact.email'), value: contact.email, href: contact.email ? `mailto:${contact.email}` : '', icon: EnvelopeIcon },
      { key: 'address', label: t('contact.address'), value: address, href: '', icon: MapPinIcon },
      { key: 'website', label: t('contact.website'), value: contact.website, href: contact.website || '', icon: GlobeAltIcon },
      { key: 'instagram', label: 'Instagram', value: contact.instagram, href: contact.instagram || '', icon: GlobeAltIcon },
      { key: 'snapchat', label: 'Snapchat', value: contact.snapchat, href: contact.snapchat || '', icon: GlobeAltIcon },
      { key: 'tiktok', label: 'TikTok', value: contact.tiktok, href: contact.tiktok || '', icon: GlobeAltIcon },
      { key: 'x', label: 'X', value: contact.x, href: contact.x || '', icon: GlobeAltIcon },
      { key: 'facebook', label: 'Facebook', value: contact.facebook, href: contact.facebook || '', icon: GlobeAltIcon },
      { key: 'youtube', label: 'YouTube', value: contact.youtube, href: contact.youtube || '', icon: GlobeAltIcon }
    ];
    return rows.filter((r) => r.value && String(r.value).trim() !== '');
  }, [contact, language, t]);

  if (loading) {
    return (
      <section className='main-view'>
        <div className='container-fluid'><p className='contact-loading'>{t('products.loading')}</p></div>
        <FooterNote />
      </section>
    );
  }

  return (
    <section className='main-view'>
      <section className='contact-page'>
        <div className='container-fluid'>
          <div className='contact-page-content'>
            <div className='contact-header'>
              <button className='back-btn' onClick={() => navigate('/')}>
                <ArrowLeftIcon className='icon' /> 
              </button>
              <h1>{t('nav.contact')}</h1>
            </div>

            <div className='contact-list'>
              {items.length === 0 ? (
                <p className='contact-empty'>{t('contact.noContactInfo')}</p>
              ) : (
                items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.key} className='contact-item'>
                      <Icon className='contact-icon' />
                      <div className='contact-meta'>
                        <span className='contact-label'>{item.label}</span>
                        {item.href ? (
                          <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel='noreferrer'>
                            {item.value}
                          </a>
                        ) : (
                          <span className='contact-value'>{item.value}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </section>
      <FooterNote />
    </section>
  );
};

export default ContactPage;
